import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import api from "@/lib/api";
import { formatSEK, rangePreset } from "@/lib/utils-app";
import { Label } from "@/components/ui/label";
import { Receipt, Carrot, Cow, Scales, Briefcase, UserCircle, Buildings, Coins, PiggyBank, CurrencyCircleDollar, ArrowsLeftRight, Warning } from "@phosphor-icons/react";
import DateRangeBar from "@/pages/DateRangeBar";
import { useData } from "@/lib/dataStore";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const pct = (n) => {
  const v = Number(n) * 100;
  // Show up to 2 decimals, trim trailing zeros (e.g. 28.97% but 25%, 20.6%)
  return `${v.toFixed(2).replace(/\.?0+$/, "")}%`;
};

export default function TaxPage() {
  const { sales, expenses, settings, refreshSettings } = useData();
  const [range, setRange] = useState({ preset: "year", ...rangePreset("year") });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState(settings?.business_entity_type || "EF");
  const [saving, setSaving] = useState(false);

  // Keep local toggle in sync with persisted setting
  useEffect(() => {
    if (settings?.business_entity_type && settings.business_entity_type !== entity) {
      setEntity(settings.business_entity_type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.business_entity_type]);

  // Persist entity choice to settings so P&L + future filings use the same mode.
  const updateEntity = async (next) => {
    setEntity(next);
    setSaving(true);
    try {
      await api.put("/settings", { business_entity_type: next });
      await refreshSettings();
      toast.success(`Tax mode: ${next === "EF" ? "Enskild Firma" : "Aktiebolag (AB)"}`);
    } catch (e) {
      toast.error("Could not save entity type");
    } finally {
      setSaving(false);
    }
  };

  // Fetch both tax + profit for the unified dashboard
  useEffect(() => {
    const params = {};
    if (range.start) params.start = range.start;
    if (range.end) params.end = range.end;
    setLoading(true);
    api.get("/analytics/profit", { params }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [range, sales.length, expenses.length, entity]);

  const vatCards = useMemo(() => {
    if (!data) return null;
    return [
      { title: "Output Moms (Collected)", subtitle: "Sales VAT — 6% + 25%", value: data.output_moms, Icon: Carrot, accent: "emerald", testId: "vat-output-card" },
      { title: "Input Moms (Paid)", subtitle: "Expense VAT — reclaimable", value: data.input_moms, Icon: Cow, accent: "blue", testId: "vat-input-card" },
      {
        title: data.net_vat_to_pay >= 0 ? "Net VAT to Pay" : "Net VAT Refund",
        subtitle: data.net_vat_to_pay >= 0 ? "Output − Input · owed to Skatteverket" : "Output − Input · refundable",
        value: Math.abs(data.net_vat_to_pay),
        Icon: Scales,
        accent: data.net_vat_to_pay >= 0 ? "red" : "emerald",
        testId: "vat-net-card",
        emphasis: true,
      },
    ];
  }, [data]);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Tab 3</p>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900">Tax Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Swedish Skatteverket · Moms (VAT) + Entity-aware income tax projection</p>
      </div>

      {/* Business Entity selector ------------------------------------------- */}
      <Card className="border-slate-200" data-testid="entity-selector-card">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold flex items-center gap-1.5">
                <Briefcase size={12} weight="duotone" /> Business Entity
              </Label>
              <p className="text-sm text-slate-600 mt-1.5 leading-snug max-w-md">
                Switch between <strong>Enskild Firma</strong> (sole prop · personal income tax) and <strong>Aktiebolag</strong> (AB · corporate tax 20,6 %). Choice persists and drives both the P&amp;L &amp; this dashboard.
              </p>
            </div>
            <Tabs value={entity} onValueChange={updateEntity} data-testid="entity-tabs">
              <TabsList className="bg-slate-100 p-1 h-auto">
                <TabsTrigger
                  value="EF"
                  className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-4 py-2 gap-1.5"
                  data-testid="entity-tab-ef"
                  disabled={saving}
                >
                  <UserCircle size={16} weight="duotone" /> Enskild Firma
                </TabsTrigger>
                <TabsTrigger
                  value="AB"
                  className="data-[state=active]:bg-blue-800 data-[state=active]:text-white px-4 py-2 gap-1.5"
                  data-testid="entity-tab-ab"
                  disabled={saving}
                >
                  <Buildings size={16} weight="duotone" /> Aktiebolag
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      <DateRangeBar range={range} setRange={setRange} />

      {/* VAT Section (identical for both entities) -------------------------- */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl text-slate-900 flex items-center gap-2">
            <ArrowsLeftRight size={20} weight="duotone" className="text-blue-700" /> VAT (Moms) · identical for EF &amp; AB
          </h2>
          {data && (
            <span className="text-xs text-slate-500" data-testid="vat-period-summary">
              {data.sales_count} sales · {data.expenses_count} expenses
            </span>
          )}
        </div>
        {loading || !vatCards ? (
          <SkeletonGrid cols={3} />
        ) : (
          <div className="grid gap-4 md:grid-cols-3">{vatCards.map((b) => <StatCard key={b.title} {...b} />)}</div>
        )}
        {data && (
          <Card className="border-slate-200 mt-4">
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <KV label="Sales 6 % (net)" value={formatSEK(data.net_revenue_human_food || 0)} accent="emerald" />
                <KV label="Sales 25 % (net)" value={formatSEK(data.net_revenue_animal_other || 0)} accent="amber" />
                <KV label="Expenses gross" value={formatSEK(data.total_expenditure_gross)} />
                <KV label="Expenses net" value={formatSEK(data.total_expenditure_net)} />
              </div>
              <p className="text-[11px] text-slate-500 mt-3 leading-snug">
                <strong>Output Moms</strong> = VAT collected on sales · <strong>Input Moms</strong> = VAT paid on operational expenses (reclaimable) · <strong>Net VAT</strong> = Output − Input.
                Negative net means Skatteverket refunds you.
              </p>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Income / Corporate Tax Section ------------------------------------- */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xl text-slate-900 flex items-center gap-2">
            {entity === "EF" ? <UserCircle size={20} weight="duotone" className="text-emerald-700" /> : <Buildings size={20} weight="duotone" className="text-blue-800" />}
            {entity === "EF" ? "Enskild Firma — Personal Income Tax" : "Aktiebolag — Corporate Tax (Bolagsskatt)"}
          </h2>
        </div>

        {loading || !data ? <SkeletonGrid cols={3} /> : entity === "EF" ? <EFCards d={data} /> : <ABCards d={data} />}
      </section>

      {/* Common P&L footing ------------------------------------------------- */}
      {data && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Receipt size={20} weight="duotone" className="text-slate-700" />
              Profit calculation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            <Row label="Total Sales (excl. VAT)" value={formatSEK(data.net_revenue)} />
            <Row label="Total Expenses (excl. VAT)" value={`− ${formatSEK(data.total_expenditure_net)}`} />
            <hr className="border-slate-200 my-1.5" />
            <Row
              label="Net Profit Before Tax"
              value={formatSEK(data.net_profit_before_tax)}
              bold
              accent={data.net_profit_before_tax >= 0 ? "emerald" : "red"}
            />
            {data.net_profit_before_tax <= 0 && (
              <p className="text-xs text-amber-700 mt-2 flex items-center gap-1.5" data-testid="loss-note">
                <Warning size={14} weight="duotone" /> Net loss — no income/corporate tax owed this period.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Entity-specific projection cards                                            */
/* -------------------------------------------------------------------------- */

function EFCards({ d }) {
  const ef = d.ef || {};
  const isLoss = ef.is_loss;
  return (
    <>
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          title="Egenavgifter"
          subtitle={`Self-employed contributions · ${pct(ef.egenavgifter_rate || 0.2897)}`}
          value={ef.egenavgifter}
          Icon={Coins}
          accent="amber"
          testId="ef-egenavgifter-card"
        />
        <StatCard
          title="Inkomstskatt"
          subtitle={`Municipal income tax · ${pct(ef.municipal_tax_rate || 0.32)}`}
          value={ef.inkomstskatt}
          Icon={CurrencyCircleDollar}
          accent="blue"
          testId="ef-inkomstskatt-card"
        />
        <StatCard
          title={isLoss ? "Total Tax Owed" : "Total Personal Tax"}
          subtitle="Egenavgifter + Inkomstskatt"
          value={ef.total_tax}
          Icon={Scales}
          accent="red"
          emphasis
          testId="ef-total-tax-card"
        />
      </div>

      <Card className="border-emerald-200 bg-emerald-50/40 mt-4" data-testid="ef-reserve-card">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
              <PiggyBank size={22} weight="duotone" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-800 font-bold">Recommended Tax Reserve</div>
              <p className="text-xs text-emerald-900/70 mt-0.5">Hold 40 – 45 % of net profit aside to safely cover both egenavgifter and municipal tax.</p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Low (40 %)</div>
                  <div className="font-heading text-xl text-emerald-800" data-testid="ef-reserve-low">{formatSEK(ef.tax_reserve_low)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">High (45 %)</div>
                  <div className="font-heading text-xl text-emerald-800" data-testid="ef-reserve-high">{formatSEK(ef.tax_reserve_high)}</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 mt-3">
        <CardContent className="p-4 text-xs text-slate-600 space-y-1">
          <div className="font-semibold text-slate-700 mb-1">Calculation breakdown</div>
          <div>1 · Net Profit Before Tax = Sales (excl. VAT) − Expenses (excl. VAT) = <span className="font-mono text-slate-900">{formatSEK(ef.net_profit_before_tax)}</span></div>
          <div>2 · Egenavgifter = NP × {pct(ef.egenavgifter_rate || 0.2897)} = <span className="font-mono text-slate-900">{formatSEK(ef.egenavgifter)}</span></div>
          <div>3 · Inkomstskatt = (NP − Egenavgifter) × {pct(ef.municipal_tax_rate || 0.32)} = <span className="font-mono text-slate-900">{formatSEK(ef.inkomstskatt)}</span></div>
          <div>4 · Personal income after tax = <span className="font-mono text-slate-900">{formatSEK(ef.personal_income_after_tax)}</span></div>
        </CardContent>
      </Card>
    </>
  );
}

function ABCards({ d }) {
  const ab = d.ab || {};
  return (
    <>
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          title="Pre-Tax Accounting Profit"
          subtitle="Sales (excl. VAT) − Expenses (excl. VAT)"
          value={ab.pre_tax_profit}
          Icon={Receipt}
          accent="blue"
          testId="ab-pretax-card"
        />
        <StatCard
          title="Bolagsskatt"
          subtitle={`Corporate tax · ${pct(ab.bolagsskatt_rate || 0.206)}`}
          value={ab.bolagsskatt}
          Icon={Scales}
          accent="red"
          emphasis
          testId="ab-bolagsskatt-card"
        />
        <StatCard
          title="Net Capital inside AB"
          subtitle="Pre-Tax Profit − Bolagsskatt"
          value={ab.net_capital_inside_ab}
          Icon={Buildings}
          accent="emerald"
          testId="ab-net-capital-card"
        />
      </div>

      <Card className="border-slate-200 mt-3">
        <CardContent className="p-4 text-xs text-slate-600 space-y-1">
          <div className="font-semibold text-slate-700 mb-1">Calculation breakdown</div>
          <div>1 · Pre-Tax Profit = Sales (excl. VAT) − Expenses (excl. VAT) = <span className="font-mono text-slate-900">{formatSEK(ab.pre_tax_profit)}</span></div>
          <div>2 · Bolagsskatt = Pre-Tax Profit × {pct(ab.bolagsskatt_rate || 0.206)} = <span className="font-mono text-slate-900">{formatSEK(ab.bolagsskatt)}</span></div>
          <div>3 · Net capital inside AB = <span className="font-mono text-slate-900">{formatSEK(ab.net_capital_inside_ab)}</span></div>
          <div className="text-slate-500 italic mt-2">Any salary you take out of the AB is treated as a regular expense in step 1 — it reduces Pre-Tax Profit before the 20,6 % is applied.</div>
        </CardContent>
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Tiny shared atoms                                                            */
/* -------------------------------------------------------------------------- */

const StatCard = ({ title, subtitle, value, Icon, accent, emphasis, testId }) => {
  const tone = {
    emerald: { bg: "bg-emerald-50 border-emerald-200 text-emerald-700", main: "text-emerald-800" },
    amber:   { bg: "bg-amber-50 border-amber-200 text-amber-700",       main: "text-amber-800" },
    red:     { bg: "bg-red-50 border-red-200 text-red-700",             main: "text-red-700" },
    blue:    { bg: "bg-blue-50 border-blue-200 text-blue-700",          main: "text-blue-800" },
  }[accent] || { bg: "bg-slate-50 border-slate-200 text-slate-700", main: "text-slate-900" };
  return (
    <Card data-testid={testId} className={`relative overflow-hidden border ${emphasis ? "border-red-300 ring-1 ring-red-200" : "border-slate-200"}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">{title}</div>
            <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
          </div>
          <div className={`w-10 h-10 rounded-lg ${tone.bg} flex items-center justify-center shrink-0`}>
            <Icon size={22} weight="duotone" />
          </div>
        </div>
        <div className={`font-heading text-3xl md:text-4xl font-bold mt-4 ${emphasis ? tone.main : "text-slate-900"}`}>
          {formatSEK(value || 0)}
        </div>
      </CardContent>
    </Card>
  );
};

const KV = ({ label, value, accent }) => {
  const tone = {
    emerald: "text-emerald-800",
    amber: "text-amber-800",
  }[accent] || "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className={`font-heading font-semibold ${tone} mt-0.5`}>{value}</div>
    </div>
  );
};

const Row = ({ label, value, bold, accent }) => {
  const tone = { emerald: "text-emerald-800", red: "text-red-700" }[accent] || "text-slate-900";
  return (
    <div className="flex items-center justify-between">
      <span className={`${bold ? "font-heading font-bold text-slate-900" : "text-slate-600"}`}>{label}</span>
      <span className={`${bold ? `font-heading font-bold text-lg ${tone}` : "font-medium text-slate-900"}`}>{value}</span>
    </div>
  );
};

const SkeletonGrid = ({ cols = 3 }) => (
  <div className={`grid gap-4 md:grid-cols-${cols}`}>
    {Array.from({ length: cols }).map((_, i) => (
      <div key={i} className="h-32 rounded-lg bg-slate-100 animate-pulse" />
    ))}
  </div>
);
